/**
 * Account Type Definitions
 * 
 * TypeScript interfaces and enums for the Accounts Management feature
 * Following the data model from the specification
 */

export enum AccountType {
  BANK_CASH = 'bank_cash',
  SAVINGS = 'savings',
  CREDIT_CARD = 'credit_card',
  LOAN = 'loan',
  INVESTMENTS_SUPERANNUATION = 'investments_superannuation',
  ASSETS = 'assets'
}

// Asset-specific enums (defined before Account interface for type references)
export enum AssetCategory {
  PROPERTY = 'property',
  VEHICLE = 'vehicle',
  VALUABLES = 'valuables',
  OTHER = 'other'
}

export enum AssetSubType {
  // Property
  HOUSE = 'house',
  APARTMENT = 'apartment',
  LAND = 'land',
  // Vehicle
  CAR = 'car',
  MOTORBIKE = 'motorbike',
  BOAT = 'boat',
  // Valuables
  JEWELLERY = 'jewellery',
  ART = 'art',
  COLLECTIBLES = 'collectibles',
  // Other
  OTHER = 'other'
}

export enum ValuationSource {
  MANUAL = 'manual',
  MARKET_ESTIMATE = 'market_estimate',
  APPRAISAL = 'appraisal',
  INSURANCE = 'insurance',
  THIRD_PARTY = 'third_party'
}

export enum ValueBehaviour {
  APPRECIATES = 'appreciates',
  DEPRECIATES = 'depreciates',
  MIXED = 'mixed'
}

export enum DepreciationMethod {
  STRAIGHT_LINE = 'straight_line',
  PERCENT_PER_YEAR = 'percent_per_year'
}

export enum LoanPurpose {
  PERSONAL = 'personal',
  CAR = 'car',
  MORTGAGE = 'mortgage',
  NOVATED_LEASE = 'novated_lease',
  OTHER = 'other'
}

export interface Account {
  id: string;
  userId: string;
  name: string;
  type: AccountType;
  provider: string;
  accountNumber: string;
  openingBalance: number;
  currentBalance: number;
  openingDate: string;
  isActive: boolean;
  closureDate?: string;
  closureReason?: string;
  includeInNetWorth: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  
  // Type-specific fields
  creditLimit?: number;
  interestRate?: number;
  paymentDueDate?: string;
  renewalDate?: string;
  loanAmount?: number;
  lastPaymentDue?: string;
  interestCalculatedDay?: number;
  loanPurpose?: LoanPurpose;
  linkedAccountId?: string | null;
  leaseEndDate?: string;
  residualValue?: number;
  employerName?: string;
  
  // Asset-specific fields (when type === 'assets')
  category?: AssetCategory;
  subType?: AssetSubType;
  currentValue?: number;
  purchasePrice?: number;
  purchaseDate?: string;
  valuationSource?: ValuationSource;
  valuationProvider?: string;
  lastValuationDate?: string;
  // v2 fields (stored but not exposed in v1 UI)
  valueBehaviour?: ValueBehaviour;
  depreciationMethod?: DepreciationMethod;
  depreciationRatePercent?: number;
  autoUpdateValue?: boolean;
}

export interface AccountBalance {
  id: string;
  accountId: string;
  balance: number;
  date: string;
  source: 'manual' | 'transaction' | 'import';
  notes?: string;
  createdAt: string;
}

export interface CreateAccountRequest {
  name: string;
  type: AccountType;
  provider: string;
  accountNumber: string;
  openingBalance: number;
  currentBalance?: number; // Optional, defaults to openingBalance if not provided
  openingDate: string;
  includeInNetWorth?: boolean;
  notes?: string;
  
  // Type-specific fields
  creditLimit?: number;
  interestRate?: number;
  paymentDueDate?: string;
  renewalDate?: string;
  loanAmount?: number;
  lastPaymentDue?: string;
  interestCalculatedDay?: number;
  loanPurpose?: LoanPurpose;
  linkedAccountId?: string | null;
  leaseEndDate?: string;
  residualValue?: number;
  employerName?: string;
  
  // Asset-specific fields (when type === AccountType.ASSETS)
  category?: AssetCategory;
  subType?: AssetSubType;
  currentValue?: number;
  purchasePrice?: number;
  purchaseDate?: string;
  valuationSource?: ValuationSource;
  valuationProvider?: string;
  lastValuationDate?: string;
  // v2 fields (stored but not exposed in v1 UI)
  valueBehaviour?: ValueBehaviour;
  depreciationMethod?: DepreciationMethod;
  depreciationRatePercent?: number;
  autoUpdateValue?: boolean;
}

export interface UpdateAccountRequest {
  type?: AccountType;
  accountNumber?: string;
  openingBalance?: number;
  openingDate?: string;
  name?: string;
  provider?: string;
  currentBalance?: number;
  isActive?: boolean;
  closureDate?: string;
  closureReason?: string;
  includeInNetWorth?: boolean;
  notes?: string;
  
  // Type-specific fields
  creditLimit?: number;
  interestRate?: number;
  paymentDueDate?: string;
  renewalDate?: string;
  loanAmount?: number;
  lastPaymentDue?: string;
  interestCalculatedDay?: number;
  loanPurpose?: LoanPurpose;
  linkedAccountId?: string | null;
  leaseEndDate?: string;
  residualValue?: number;
  employerName?: string;
  
  // Asset-specific fields (when type === 'assets')
  category?: AssetCategory;
  subType?: AssetSubType;
  currentValue?: number;
  purchasePrice?: number;
  purchaseDate?: string;
  valuationSource?: ValuationSource;
  valuationProvider?: string;
  lastValuationDate?: string;
  // v2 fields (stored but not exposed in v1 UI)
  valueBehaviour?: ValueBehaviour;
  depreciationMethod?: DepreciationMethod;
  depreciationRatePercent?: number;
  autoUpdateValue?: boolean;
}

// Asset-specific request interfaces
export interface CreateAssetRequest {
  name: string;
  type: AccountType.ASSETS;
  category: AssetCategory;
  subType: AssetSubType;
  currentValue: number;
  purchasePrice?: number;
  purchaseDate?: string;
  valuationSource?: ValuationSource;
  valuationProvider?: string;
  lastValuationDate?: string;
  includeInNetWorth?: boolean;
  notes?: string;
  provider?: string;
}

export interface UpdateAssetRequest {
  name?: string;
  category?: AssetCategory;
  subType?: AssetSubType;
  currentValue?: number;
  purchasePrice?: number;
  purchaseDate?: string;
  valuationSource?: ValuationSource;
  valuationProvider?: string;
  lastValuationDate?: string;
  includeInNetWorth?: boolean;
  notes?: string;
  provider?: string;
}

// Alias for frontend components
export type CreateAccountPayload = CreateAccountRequest;
export type UpdateAccountPayload = UpdateAccountRequest;

export interface CloseAccountRequest {
  closureDate: string;
  closureReason?: string;
  includeInNetWorth?: boolean;
  currentValue?: number;
}

export interface AccountListResponse {
  accounts: Account[];
  pagination: Pagination;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface AccountListParams {
  type?: AccountType;
  status?: 'active' | 'closed';
  includeInNetWorth?: boolean;
  sortBy?: 'name' | 'type' | 'balance' | 'dateCreated' | 'provider' | 'currentValue';
  category?: AssetCategory;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

export interface ValidationError {
  code: string;
  message: string;
  errors: ValidationErrorDetail[];
}

export interface ValidationErrorDetail {
  field: string;
  message: string;
  code: string;
}

// Account type specific field requirements
export const ACCOUNT_TYPE_REQUIREMENTS: Record<AccountType, string[]> = {
  [AccountType.BANK_CASH]: [],
  [AccountType.SAVINGS]: [],
  [AccountType.CREDIT_CARD]: ['creditLimit', 'paymentDueDate'],
  [AccountType.LOAN]: ['loanAmount', 'interestRate', 'paymentDueDate'],
  [AccountType.INVESTMENTS_SUPERANNUATION]: [],
  [AccountType.ASSETS]: ['category', 'subType', 'currentValue']
};

// Account type specific optional fields
export const ACCOUNT_TYPE_OPTIONAL_FIELDS: Record<AccountType, string[]> = {
  [AccountType.BANK_CASH]: [],
  [AccountType.SAVINGS]: ['interestRate', 'interestCalculatedDay'],
  [AccountType.CREDIT_CARD]: ['interestRate', 'renewalDate'],
  [AccountType.LOAN]: ['lastPaymentDue', 'loanPurpose', 'linkedAccountId', 'leaseEndDate', 'residualValue', 'employerName'],
  [AccountType.INVESTMENTS_SUPERANNUATION]: ['interestRate'],
  [AccountType.ASSETS]: ['purchasePrice', 'purchaseDate', 'provider', 'notes']
};
